import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { getInitials } from '../../../components/ui/Avatar';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  PRIMARY_ADDRESS_HOME,
  PRIMARY_ADDRESS_WORK,
  isPrimaryAddress,
} from '../../../utils/employeeAddress';
import { displayValue, formatDate, formatIsoDate, getFilterValueLabel, isEmptyValue, isYes } from '../employeeColumns';
import { nationalityLabel } from '../employeeForm/employeeFormOptions';

const YES_NO_COLUMNS = ['isHandicappedPerson', 'isIntern', 'isRetired'];

const hasStatus = (value) => !(value === undefined || value === null || isNaN(value));

/** Plain-text form of a cell, for its tooltip. */
export const getCellTitle = (key, employee) => {
  const value = employee[key];
  if (key === 'Active' || YES_NO_COLUMNS.includes(key)) return undefined;
  if (value === undefined || value === null || typeof value === 'object') return undefined;
  return String(value);
};

/** Content of one employee table cell. */
const EmployeeCell = ({ columnKey, employee, pendingLeaveCount }) => {
  const { t, locale } = useTranslation();
  const value = employee[columnKey];

  if (columnKey === 'eName') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span
          aria-hidden="true"
          className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400/90 tw-to-cyan-600/90 tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
        >
          {getInitials(employee.eName, employee.eSurname)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{displayValue(value)}</span>
        {pendingLeaveCount > 0 && (
          <span
            title={t('employees.table.pendingLeave', { count: pendingLeaveCount })}
            className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-amber-700 dark:tw-text-amber-300"
          >
            <LuCalendarClock aria-hidden="true" className="tw-h-3 tw-w-3" />
            {pendingLeaveCount}
            <span className="tw-sr-only"> {t('employees.table.pendingLeaveSr')}</span>
          </span>
        )}
      </span>
    );
  }

  if (columnKey === 'Active') {
    if (!hasStatus(value)) return <span className="tw-text-slate-400">-</span>;
    return value === 1 ? (
      <Badge tone="success">
        <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-500" />
        {t('employees.status.active')}
      </Badge>
    ) : (
      <Badge tone="neutral">
        <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-slate-400" />
        {t('employees.status.inactive')}
      </Badge>
    );
  }

  if (YES_NO_COLUMNS.includes(columnKey)) {
    // Kayıtlarda true/false ya da 'Yes'/'No' bulunabilir
    return isYes(value)
      ? <span className="tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{t('employees.yes')}</span>
      : <span className="tw-text-slate-500 dark:tw-text-slate-400">{t('employees.no')}</span>;
  }

  if (columnKey === 'address' || columnKey === 'workAddress') {
    // Çalışanın profilinden seçtiği ana adres
    const primary = isPrimaryAddress(employee, columnKey === 'address' ? PRIMARY_ADDRESS_HOME : PRIMARY_ADDRESS_WORK);
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
        <span className="tw-min-w-0 tw-truncate">{displayValue(value)}</span>
        {primary && <Badge tone="info" className="tw-text-[10px]">{t('employees.table.primaryAddress')}</Badge>}
      </span>
    );
  }

  let text = displayValue(value);
  if (columnKey === 'nationality' && !isEmptyValue(value)) text = value === 173 ? 'Turkey' : nationalityLabel(t, value);
  if (columnKey === 'employeeExitDate') text = formatDate(value, locale);
  if (columnKey === 'birthday' || columnKey === 'startDate') text = formatIsoDate(value, locale);
  if ((columnKey === 'workType' || columnKey === 'educationInfo') && !isEmptyValue(value)) text = getFilterValueLabel(t, columnKey, value);

  return <span className={`tw-block tw-truncate ${text === '-' ? 'tw-text-slate-400 dark:tw-text-slate-500' : ''}`}>{text}</span>;
};

EmployeeCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  employee: PropTypes.object.isRequired,
  pendingLeaveCount: PropTypes.number,
};

export default React.memo(EmployeeCell);
